import { countrySelector, validators } from '@vivaldis/common'
import { EmployeeInput } from '@vivaldis/graphql'
import type { RcFile } from 'antd/es/upload/interface'
import { format } from 'date-fns'
import {
  Formik,
  FormikContextType,
  FormikHelpers,
  useFormikContext
} from 'formik'
import { PropsWithChildren, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { fileToBase64 } from '../../../../../utils/fileToBase64'
import { CountryType } from '../../../types/CountryType'
import { Employee } from '../../../types/Employee'

export interface EditEmployeeProfileFormValues {
  firstName: string
  lastName: string
  nationalityCountryId: string | null
  ssn: string
  // -> id card
  // NOTE: User can not change idCardFrontPicture and idCardBackPicture (only way to change it is to go via UploadIdCardStep)
  idCardNumber: string | null
  idCardExpiresOn: Date | null
  // -> residence permit
  residencePermitFrontPictureFile: RcFile | null
  residencePermitBackPictureFile: RcFile | null
  residencePermitExpiresOn: Date | null
  residencePermitCode: string | null
  // work permit
  workPermitFrontPictureFile: RcFile | null
  workPermitBackPictureFile: RcFile | null
  workPermitExpiresOn: Date | null
}

export function useForm(): FormikContextType<EditEmployeeProfileFormValues> {
  return useFormikContext()
}

export interface EmployeeFormProviderProps {
  countries: CountryType[]
  employee: Employee
  onGoBack: () => void
  onSubmit: (
    employeeInput: EmployeeInput,
    formikHelpers: FormikHelpers<EditEmployeeProfileFormValues>
  ) => Promise<void>
}

export const FormProvider = ({
  countries,
  employee,
  onSubmit,
  onGoBack,
  children
}: PropsWithChildren<EmployeeFormProviderProps>) => {
  const [t] = useTranslation('web_ui')

  const initialFormValues: EditEmployeeProfileFormValues = useMemo(
    () => ({
      firstName: employee.firstName || '',
      lastName: employee.lastName || '',
      nationalityCountryId: employee.profile?.nationalityCountry?.id || null,
      ssn: employee.ssn || '',
      // id card
      idCardNumber: employee.profile?.idCardNumber || null,
      idCardExpiresOn: employee.profile?.idCardExpiresOn
        ? new Date(employee.profile?.idCardExpiresOn)
        : null,
      // -> residence permit
      residencePermitFrontPictureFile: null,
      residencePermitBackPictureFile: null,
      residencePermitExpiresOn: employee.workProfile?.residencePermitExpiresOn
        ? new Date(employee.workProfile?.residencePermitExpiresOn)
        : null,
      residencePermitCode: employee.workProfile?.residencePermitCode || null,
      // -> work permit
      workPermitExpiresOn: employee.workProfile?.workPermitExpiresOn
        ? new Date(employee.workProfile?.workPermitExpiresOn)
        : null,
      workPermitFrontPictureFile: null,
      workPermitBackPictureFile: null
    }),
    [
      employee.firstName,
      employee.lastName,
      employee.profile?.idCardExpiresOn,
      employee.profile?.idCardNumber,
      employee.profile?.nationalityCountry?.id,
      employee.ssn,
      employee.workProfile?.residencePermitCode,
      employee.workProfile?.residencePermitExpiresOn,
      employee.workProfile?.workPermitExpiresOn
    ]
  )

  const isEeeMemberCountry = useCallback(
    (countryId: string): boolean =>
      !!countries.find(country => country.id === countryId)?.eeaMember,
    [countries]
  )

  const ValidationSchema = useMemo(
    () =>
      Yup.object().shape({
        // --------- GeneralInformation
        firstName: Yup.string()
          .label(
            t(
              'edit_employee_personal_information_form.steps.edit_manually.first_name.label'
            )
          )
          .required(),
        lastName: Yup.string()
          .label(
            t(
              'edit_employee_personal_information_form.steps.edit_manually.last_name.label'
            )
          )
          .required(),
        nationalityCountryId: Yup.string()
          .label(
            t(
              'edit_employee_personal_information_form.steps.edit_manually.nationality_country_id.label'
            )
          )
          .nullable()
          .required(),
        ssn: Yup.string()
          .label(
            t(
              'edit_employee_personal_information_form.steps.edit_manually.ssn.label'
            )
          )
          .nullable()
          .required()
          .test(
            'isValidSSN',
            t(
              'edit_employee_personal_information_form.steps.edit_manually.ssn.errors.invalid'
            ),
            ssn => (ssn ? validators.isValidSSN(ssn) : true)
          ),
        // ID card
        idCardNumber: Yup.string()
          .label(
            t(
              'edit_employee_personal_information_form.steps.edit_manually.id_card_number.label'
            )
          )
          .nullable()
          .when('nationalityCountryId', ([nationalityCountryId], schema) => {
            if (!nationalityCountryId) {
              return schema
            }

            if (isEeeMemberCountry(nationalityCountryId)) {
              return schema
                .required()
                .test(
                  'isValidIdCard',
                  t(
                    'edit_employee_personal_information_form.steps.edit_manually.id_card_number.errors.invalid'
                  ),
                  (idCardNumber: string) => {
                    const isBelgium =
                      countrySelector(nationalityCountryId, countries)
                        ?.nameEn === 'Belgium'
                    if (isBelgium) {
                      return !!(
                        idCardNumber &&
                        validators.isValidBelgiumIdCardNumber(idCardNumber)
                      )
                    }
                    return true
                  }
                )
            }

            return schema
          }),
        idCardExpiresOn: Yup.date()
          .label(
            t(
              'edit_employee_personal_information_form.steps.edit_manually.id_card_expires_on.label'
            )
          )
          .nullable()
          .when('nationalityCountryId', ([nationalityCountryId], schema) => {
            if (!nationalityCountryId) {
              return schema
            }

            if (isEeeMemberCountry(nationalityCountryId)) {
              return schema
                .required()
                .test(
                  'isIdCardNotExpired',
                  t(
                    'edit_employee_personal_information_form.steps.edit_manually.id_card_expires_on.errors.expired'
                  ),
                  (idCardExpiresOn: Date | null) => {
                    if (!idCardExpiresOn) {
                      return false
                    }
                    return validators.isDateInTheFuture(idCardExpiresOn)
                  }
                )
            }

            return schema
          }),
        idCardFrontPicture: Yup.string()
          .label(
            t(
              'edit_employee_personal_information_form.steps.edit_manually.id_card_front_picture.label'
            )
          )
          .nullable(),
        idCardBackPicture: Yup.string()
          .label(
            t(
              'edit_employee_personal_information_form.steps.edit_manually.id_card_back_picture.label'
            )
          )
          .nullable(),
        // Residence permit
        residencePermitCode: Yup.string()
          .label(
            t(
              'edit_employee_personal_information_form.steps.edit_manually.residence_permit_code.label'
            )
          )
          .nullable()
          .when('nationalityCountryId', ([nationalityCountryId], schema) => {
            return !isEeeMemberCountry(nationalityCountryId)
              ? schema.required()
              : schema.notRequired()
          }),
        residencePermitExpiresOn: Yup.date()
          .label(
            t(
              'edit_employee_personal_information_form.steps.edit_manually.residence_permit_expires_on.label'
            )
          )
          .nullable()
          .when('nationalityCountryId', ([nationalityCountryId], schema) => {
            return !isEeeMemberCountry(nationalityCountryId)
              ? schema
                  .required()
                  .test(
                    'isResidencePermitNotExpired',
                    t(
                      'edit_employee_personal_information_form.steps.edit_manually.residence_permit_expires_on.errors.expired'
                    ),
                    (residencePermitExpiresOn: Date | null) => {
                      if (!residencePermitExpiresOn) {
                        return false
                      }
                      return validators.isDateInTheFuture(
                        residencePermitExpiresOn
                      )
                    }
                  )
              : schema.notRequired()
          }),
        residencePermitFrontPictureFile: Yup.string()
          .label(
            t(
              'edit_employee_personal_information_form.steps.edit_manually.residence_permit_front_picture.label'
            )
          )
          .nullable()
          .when({
            is: (residencePermitFrontPictureFile: string | null) =>
              !!residencePermitFrontPictureFile ||
              !employee.workProfile?.residencePermitFrontPicture,
            then: schema =>
              schema
                .required()
                .when(
                  'nationalityCountryId',
                  ([nationalityCountryId], schema) => {
                    return !isEeeMemberCountry(nationalityCountryId)
                      ? schema.required()
                      : schema.notRequired()
                  }
                ),
            otherwise: schema => schema.notRequired()
          }),
        residencePermitBackPictureFile: Yup.string()
          .label(
            t(
              'edit_employee_personal_information_form.steps.edit_manually.residence_permit_back_picture.label'
            )
          )
          .nullable()
          .when({
            is: (residencePermitBackPictureFile: string | null) =>
              !!residencePermitBackPictureFile ||
              !employee.workProfile?.residencePermitBackPicture,
            then: schema =>
              schema
                .required()
                .when(
                  'nationalityCountryId',
                  ([nationalityCountryId], schema) => {
                    return !isEeeMemberCountry(nationalityCountryId)
                      ? schema.required()
                      : schema.notRequired()
                  }
                ),
            otherwise: schema => schema.notRequired()
          }),
        // Work permit
        workPermitFrontPictureFile: Yup.string()
          .label(
            t(
              'edit_employee_personal_information_form.steps.edit_manually.work_permit_front_picture.label'
            )
          )
          .nullable(),
        workPermitBackPictureFile: Yup.string()
          .label(
            t(
              'edit_employee_personal_information_form.steps.edit_manually.work_permit_back_picture.label'
            )
          )
          .nullable(),
        workPermitExpiresOn: Yup.date()
          .label(
            t(
              'edit_employee_personal_information_form.steps.edit_manually.work_permit_expires_on.label'
            )
          )
          .nullable()
      }),
    [
      countries,
      employee.workProfile?.residencePermitBackPicture,
      employee.workProfile?.residencePermitFrontPicture,
      isEeeMemberCountry,
      t
    ]
  )

  const handleSubmit = useCallback(
    async (
      values: EditEmployeeProfileFormValues,
      formikHelpers: FormikHelpers<EditEmployeeProfileFormValues>
    ) => {
      await onSubmit(
        {
          firstName: values.firstName,
          lastName: values.lastName,
          ssn: values.ssn,
          profile: {
            nationalityCountryId: values.nationalityCountryId,
            idCardNumber: values.idCardNumber,
            idCardExpiresOn: values.idCardExpiresOn
              ? format(values.idCardExpiresOn, 'yyyy-MM-dd')
              : null
          },
          workProfile: {
            // residence permit
            residencePermitExpiresOn: values.residencePermitExpiresOn
              ? format(values.residencePermitExpiresOn, 'yyyy-MM-dd')
              : null,
            residencePermitCode: values.residencePermitCode,
            ...(values.residencePermitFrontPictureFile
              ? {
                  residencePermitFrontPictureAttachment: {
                    fileName: values.residencePermitFrontPictureFile.name,
                    base64: await fileToBase64(
                      values.residencePermitFrontPictureFile
                    )
                  }
                }
              : {}),
            ...(values.residencePermitBackPictureFile
              ? {
                  residencePermitBackPictureAttachment: {
                    fileName: values.residencePermitBackPictureFile.name,
                    base64: await fileToBase64(
                      values.residencePermitBackPictureFile
                    )
                  }
                }
              : {}),
            // work permit
            ...(values.workPermitBackPictureFile
              ? {
                  workPermitBackPictureAttachment: {
                    fileName: values.workPermitBackPictureFile.name,
                    base64: await fileToBase64(values.workPermitBackPictureFile)
                  }
                }
              : {}),
            ...(values.workPermitFrontPictureFile
              ? {
                  workPermitFrontPictureAttachment: {
                    fileName: values.workPermitFrontPictureFile.name,
                    base64: await fileToBase64(
                      values.workPermitFrontPictureFile
                    )
                  }
                }
              : {}),
            workPermitExpiresOn: values.workPermitExpiresOn
              ? format(values.workPermitExpiresOn, 'yyyy-MM-dd')
              : null
          }
        },
        formikHelpers
      )
      formikHelpers.setSubmitting(false)
      onGoBack()
    },
    [onGoBack, onSubmit]
  )

  return (
    <Formik<EditEmployeeProfileFormValues>
      initialValues={initialFormValues}
      validateOnChange={false}
      validateOnBlur={true}
      validationSchema={ValidationSchema}
      onSubmit={handleSubmit}
    >
      {children}
    </Formik>
  )
}
